import { useNavigator } from '@repo/toolkit/use-navigator'
import ActionButton from '@repo/ui/action-button'
import Box from '@repo/ui/box'
import { DeleteIcon, PlusIcon, TravelIcon, TravelTwoIcon } from '@repo/ui/icons'
import Modal, { MODAL_BODY_CLASSNAME } from '@repo/ui/modal'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import React from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'

import { API } from '../../api/apis.config'
import { assignTripsToTripGrouping } from '../../api/trip-groupings/assign-trips-to-trip-grouping'
import { getTripGrouping } from '../../api/trip-groupings/get-trip-grouping'
import { unassignTripsFromTripGrouping } from '../../api/trip-groupings/unassign-trips-from-trip-grouping'
import { updateTripGrouping } from '../../api/trip-groupings/update-trip-grouping'
import DynamicDataTable, { DynamicTableRef } from '../../components/elements/dynamic-table'
import { ItemNotFound } from '../../components/elements/item-not-found'
import LoadingScreen from '../../components/elements/loading/loading-screen'
import UserNavBar from '../../components/elements/nav-bar'
import SearchableDataTable from '../../components/elements/searchable-data-table'
import { TripGroupingForm } from '../../components/pages/trip-groupings/trip-grouping-form'
import { Paths } from '../../lib/constants/path'
import { useModalManager } from '../../lib/hooks/use-modal-manager'
import { Trip, TripFilters } from '../../lib/types/models/trip/trip'
import { updateButtonsConfig } from '../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../lib/utils/functions/misc.functions'
import NotFoundPage from '../common/not-found'
import { getTripColumns } from '../trips/utils'

export function TripGroupingUpdatePage() {
  const { modalState, toggleModal, closeModel, selectedData } = useModalManager<
    'delete' | 'trips',
    Trip
  >(['delete', 'trips'])
  const { id } = useParams<{ id: string }>()
  const queryClient = useQueryClient()
  const tableRef = React.useRef<DynamicTableRef<Trip>>(null)
  const { navigate, back } = useNavigator()

  const { mutateAsync } = useMutation({
    mutationFn: updateTripGrouping,
    onError: showAxiosError,
    onSuccess() {
      queryClient.invalidateQueries({ exact: true, queryKey: ['trip-grouping', id] })
      toast.success('Groupe de voyage modifié avec succès')
    }
  })

  const {
    mutate: assignTripsToTripGroupingMutation,
    isPending: isAssignTripsToTripGroupingPending
  } = useMutation({
    mutationFn: assignTripsToTripGrouping,
    onError: showAxiosError,
    onSuccess() {
      tableRef.current?.refetch()
      toggleModal('trips')
      toast.success('Voyage ajouté avec succès')
    }
  })

  const {
    mutate: unassignTripsToTripGroupingMutation,
    isPending: isUnassignTripsToTripGroupingPending
  } = useMutation({
    mutationFn: unassignTripsFromTripGrouping,
    onError: showAxiosError,
    onSuccess() {
      tableRef.current?.refetch()
      closeModel('delete')
      toast.success('Voyage supprimé avec succès')
    }
  })

  const { data: tripGrouping, isLoading } = useQuery({
    queryKey: ['trip-grouping', id],
    queryFn: () => {
      if (id) return getTripGrouping(+id)
    }
  })

  if (!id || isNaN(parseInt(id)))
    return <ItemNotFound title="Identifiant de groupe de voyage invalide" />

  if (isLoading) return <LoadingScreen />
  if (!tripGrouping) return <NotFoundPage />

  return (
    <div>
      <UserNavBar title={'Modifier les groupes de voyage'} icon={TravelIcon} />
      <div className={'h-4'} />
      <Box>
        <div className={'h-1'} />
        <TripGroupingForm
          defaultValues={{ name: tripGrouping.name, year: tripGrouping.year }}
          buttonsConfig={updateButtonsConfig({
            onCancel: () => back(Paths.TRIP_GROUPING_LIST_PAGE),
            onUpdate: (values) => mutateAsync({ id: tripGrouping.id, ...values }),
            onUpdateThenCreate: async (values) => {
              await mutateAsync({ id: tripGrouping.id, ...values })
              navigate(Paths.TRIP_GROUPING_CREATE_PAGE)
            },
            onUpdateThenList: async (values) => {
              await mutateAsync({ id: tripGrouping.id, ...values })
              navigate(Paths.TRIP_GROUPING_LIST_PAGE)
            }
          })}
        />
      </Box>

      <div className={'h-4'} />

      <DynamicDataTable<Trip, TripFilters>
        ref={tableRef}
        title={'Tous les voyages'}
        deleteMessage={{
          name: 'voyages',
          attributeName: 'id',
          deleteOne: 'Êtes-vous sûr de vouloir supprimer le voyage ?',
          deleteMany: 'Êtes-vous sûr de vouloir supprimer ces voyages ?',
          deleteAll: 'Êtes-vous sûr de vouloir supprimer tous les voyages ?'
        }}
        columns={[
          ...getTripColumns('PARTIAL'),
          {
            accessorKey: 'id',
            header: '',
            cell: ({ row }) => (
              <ActionButton
                variant={'danger'}
                icon={DeleteIcon}
                onClick={() => toggleModal('delete', row.original)}
              />
            )
          }
        ]}
        permissions={{ delete: false }}
        isSearchable={false}
        filters={{ belongsTo: [tripGrouping.id] }}
        endpoint={API.trips.getAll.url}
        actions={[
          {
            type: 'global',
            icon: PlusIcon,
            variant: 'primary',
            label: 'Ajouter un voyage',
            fn: () => toggleModal('trips')
          }
        ]}
      />

      {selectedData && (
        <Modal
          actionButtonsOptions={{
            confirmText: 'Supprimer',
            cancelText: 'Fermer',
            onConfirm: () =>
              unassignTripsToTripGroupingMutation({
                tripGroupingId: tripGrouping.id,
                tripIds: [selectedData.id]
              })
          }}
          onCancel={() => closeModel('delete')}
          isOpen={modalState.delete}
          title={'Êtes-vous sûr de vouloir supprimer ce voyage ?'}
          icon={DeleteIcon}
          isLoading={isUnassignTripsToTripGroupingPending}
          variant={'destructive'}
        />
      )}

      <Modal
        onCancel={() => closeModel('trips')}
        isOpen={modalState.trips}
        title={'Voyages'}
        icon={TravelTwoIcon}
        size="medium"
        hideActions
      >
        <SearchableDataTable<Trip, TripFilters>
          className={MODAL_BODY_CLASSNAME}
          description={`Vous pouvez choisir plus d'un voyage :`}
          selectionCountLabel={(count) => `${count} voyage(s) sélectionné(s)`}
          endpoint={API.trips.getAll.url}
          columns={getTripColumns('LIGHT')}
          filters={{ showUnassignedBelongsTo: true, belongsTo: [tripGrouping.id] }}
          onSelect={(selectedTrips) => {
            assignTripsToTripGroupingMutation({
              tripIds: selectedTrips.map((trip) => trip.id),
              tripGroupingId: tripGrouping.id
            })
          }}
          isLoading={isAssignTripsToTripGroupingPending}
          onCancel={() => closeModel('trips')}
        />
      </Modal>
    </div>
  )
}
